import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import TestCard from "components/TestCard";
import { testSections, multiSectionKeys, resolveSectionLabel } from "config/testSections";
import { useTestData } from "context/TestDataContext";
import { useTranslation } from "i18n/LanguageContext";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { mapCeradToScoresheet } from "scoring/calcCERAD";
import { computeEvaluation } from "scoring/scoringEval";
import { calcNosger } from "scoring/calcNosger";
import { calcBDQ } from "scoring/calcBDQ";
import { buildCellEdits } from "utils/scoreFillUtils";
import MenuBar from "../components/MenuBar";

const NOSGER_TEST_KEY = "NOSGER (Spiegel et al., 1999)";
const BDQ_TEST_KEY = "BDQ (Rascovsky et al., 2011)";

// Resolve resultLoc for the current language only. No cross-language fallback:
// FR row 127 is not the same test as DE row 127, so falling back FR→DE would
// silently write a verdict into an unrelated cell. Callers must handle null.
const pickLoc = (entry, lang) => {
  const loc = entry?.resultLoc;
  if (!loc) return null;
  if (typeof loc === "string") return loc;
  return loc[lang] ?? null;
};

const buildSuggestedName = (patientInfo, lang) => {
  const base = lang === "de" ? "profilBlatt" : "feuilleProfil";
  const iso = patientInfo?.dateExamen || new Date().toISOString().slice(0, 10);
  const [y, m, d] = iso.split("-");
  const dateStr = y && m && d ? `${y.slice(2)}${m}${d}` : "";
  const clean = (s) => String(s || "").trim().replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "");
  const name = [clean(patientInfo?.nom), clean(patientInfo?.prenom)].filter(Boolean).join("_");
  return [name, dateStr, base].filter(Boolean).join("_") + ".xlsx";
};

const formatGeria12Value = (value, paramName) => {
  if (value == null) return "";
  if (typeof value === "number") {
    if (paramName.endsWith("z")) return String(Math.round(value * 100) / 100);
    return String(value);
  }
  return String(value).replace(/≤\s*≤/g, "≤").replace(/≥\s*≥/g, "≥").trim();
};

export default function NewScoreSheet() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dialogInfo, setDialogInfo] = useState(null);
  const { testData, updateTest, patientInfo, selectedProfile: profile } = useTestData();
  const { t, lang } = useTranslation();
  const navigate = useNavigate();

  if (!profile) {
    navigate("/");
    return null;
  }

  const handleFillScores = async () => {
    setIsProcessing(true);

    try {
      const { cellEdits, hiddenRows, visibleRows, emptyHeaderRows, mergeRanges, skippedNoMapping } = buildCellEdits(patientInfo, testData, profile.tests, lang);
      const suggestedName = buildSuggestedName(patientInfo, lang);
      const result = await window.electronAPI.openAndEditExcel(cellEdits, hiddenRows, visibleRows, emptyHeaderRows, lang, suggestedName, mergeRanges);

      if (result.success) {
        let message = result.filePath;
        if (skippedNoMapping && skippedNoMapping.length > 0) {
          message += `\n${t("score.skippedNoDE", { count: skippedNoMapping.length })}`;
        }
        setDialogInfo({ type: "success", title: t("score.saved"), message });
      } else if (result.canceled) {
        console.log("Save operation canceled by user");
      } else {
        setDialogInfo({ type: "error", title: t("score.error"), message: result.error });
      }
    } catch (error) {
      console.error("Error filling scores:", error);
      setDialogInfo({ type: "error", title: t("score.error"), message: error.message });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBatchDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setBatchLoading(true);

    try {
      const allFiles = Array.from(e.dataTransfer.files);
      const rtfFiles = allFiles.filter((f) => f.name.toLowerCase().endsWith(".rtf"));
      const xlsFiles = allFiles.filter((f) => {
        const n = f.name.toLowerCase();
        return n.endsWith(".xls") || n.endsWith(".xlsx") || n.endsWith(".xlsm");
      });

      if (rtfFiles.length === 0 && xlsFiles.length === 0) {
        setBatchLoading(false);
        return;
      }

      const newEntries = [];

      // Process RTF files
      for (const file of rtfFiles) {
        try {
          const result = await window.electronAPI.parseDroppedRTFFile(file, lang);

          if (!result.success) {
            newEntries.push({ fileName: file.name, status: "error", error: result.error });
            continue;
          }

          const { testType, contextData } = result.data;

          if (!contextData) {
            newEntries.push({ fileName: file.name, status: "unmatched", testType: testType || "Inconnu" });
            continue;
          }

          if (profile.tests.includes(testType)) {
            const evaluation = computeEvaluation(contextData.resultData, contextData.resultLoc);
            updateTest(testType, { ...contextData, evaluation });
            newEntries.push({ fileName: file.name, status: "success", testType });
          } else {
            newEntries.push({ fileName: file.name, status: "unmatched", testType });
          }
        } catch (err) {
          newEntries.push({ fileName: file.name, status: "error", error: err.message });
        }
      }

      // Process XLS files (auto-detect CERAD vs GERIA-12 by sheet name)
      for (const file of xlsFiles) {
        try {
          const result = await window.electronAPI.parseXLSAuto(file);

          if (!result.success) {
            newEntries.push({ fileName: file.name, status: "error", error: result.error });
            continue;
          }

          if (result.type === "nosger") {
            if (!profile.tests.includes(NOSGER_TEST_KEY)) {
              newEntries.push({ fileName: file.name, status: "unmatched", testType: "NOSGER" });
              continue;
            }
            const nosgerCalc = calcNosger({
              sums: result.sums,
              thresholds: result.thresholds,
              genderCode: result.genderCode,
              patientGenre: patientInfo.genre,
              lang,
            });
            const { resultData, resultText } = nosgerCalc;
            const resultLoc = pickLoc(testData[NOSGER_TEST_KEY], lang);
            const evaluation = resultLoc
              ? computeEvaluation(resultData, resultLoc, {
                  evalRule: "nosger",
                  anyDecided: nosgerCalc.anyDecided,
                  anyAuffaellig: nosgerCalc.anyAuffaellig,
                })
              : null;
            updateTest(NOSGER_TEST_KEY, { resultData, resultText, evaluation, nosgerFile: result.fileName });
            newEntries.push({ fileName: file.name, status: "success", testType: "NOSGER" });
            continue;
          }

          if (result.type === "bdq") {
            if (!profile.tests.includes(BDQ_TEST_KEY)) {
              newEntries.push({ fileName: file.name, status: "unmatched", testType: "BDQ" });
              continue;
            }
            const bdqCalc = calcBDQ({
              scores: result.scores,
              total: result.total,
              patientGenre: patientInfo.genre,
              lang,
            });
            const { resultData, resultText } = bdqCalc;
            const resultLoc = pickLoc(testData[BDQ_TEST_KEY], lang);
            const evaluation = resultLoc
              ? computeEvaluation(resultData, resultLoc, {
                  evalRule: "bdq",
                  anyDecided: bdqCalc.anyDecided,
                  anyAuffaellig: bdqCalc.anyAuffaellig,
                })
              : null;
            updateTest(BDQ_TEST_KEY, { resultData, resultText, evaluation, bdqFile: result.fileName });
            newEntries.push({ fileName: file.name, status: "success", testType: "BDQ" });
            continue;
          }

          if (result.type === "geria12") {
            let count = 0;
            for (const testName of profile.tests) {
              const test = testData[testName];
              if (!test?.geria12Cells) continue;
              const updatedParams = (test.params || []).map((p) => {
                const cellRef = test.geria12Cells[p.name];
                if (!cellRef) return p;
                const v = result.cells[cellRef];
                if (v == null) return p;
                return { ...p, value: formatGeria12Value(v, p.name) };
              });
              updateTest(testName, { params: updatedParams, geria12File: result.fileName });
              count++;
            }
            if (count === 0) {
              newEntries.push({ fileName: file.name, status: "unmatched", testType: "GERIA-12" });
            } else {
              newEntries.push({ fileName: file.name, status: "success", testType: "GERIA-12" });
            }
            continue;
          }

          // CERAD branch
          const results = mapCeradToScoresheet(result.rows).filter((entry) =>
            profile.tests.includes(entry.testKey)
          );
          if (results.length === 0) {
            newEntries.push({ fileName: file.name, status: "unmatched", testType: "CERAD" });
            continue;
          }

          for (const entry of results) {
            updateTest(entry.testKey, {
              resultData: entry.resultData,
              resultText: entry.resultText,
              evaluation: entry.evaluation,
              uploadedFile: result.fileName,
            });
          }
          newEntries.push({ fileName: file.name, status: "success", testType: "CERAD" });
        } catch (err) {
          newEntries.push({ fileName: file.name, status: "error", error: err.message });
        }
      }

      setUploadedFiles((prev) => [...prev, ...newEntries]);
    } catch (err) {
      setUploadedFiles((prev) => [...prev, { fileName: "?", status: "error", error: err.message }]);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  return (
    <Box mx={"5%"} mt={15}>
      <MenuBar />
      <Box mt={12}>
        <Typography variant="h5" color="primary">
          {t("score.title")} : {profile.name}
        </Typography>
        <Divider />
        {profile.lang && profile.lang !== lang && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t("home.profileLangMismatch")}
          </Alert>
        )}

        {/* Batch RTF / XLS Drop Zone */}
        <Box my={5}>
          <Box
            onDrop={handleBatchDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              border: "2px dashed",
              borderColor: isDragging ? "primary.main" : "secondary.main",
              borderRadius: 2,
              backgroundColor: isDragging ? "action.hover" : "transparent",
              transition: "all 0.2s ease-in-out",
              cursor: "pointer",
              py: 3,
              px: 2,
            }}
          >
            {batchLoading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={28} />
                <Typography ml={2} color="textSecondary">
                  {t("score.analyzing")}
                </Typography>
              </Box>
            ) : (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
                <UploadFileIcon
                  sx={{
                    fontSize: 32,
                    color: isDragging ? "primary.main" : "text.secondary",
                    transition: "color 0.2s ease-in-out",
                  }}
                />
                <Typography
                  variant="body2"
                  color={isDragging ? "primary" : "textSecondary"}
                  sx={{ transition: "color 0.2s ease-in-out" }}
                >
                  {isDragging ? t("score.dragging") : t("score.dragHint")}
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={3}>
              {uploadedFiles.map((f, i) => (
                <Chip
                  key={i}
                  icon={f.status === "success" ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                  label={f.status === "success"
                    ? `${f.fileName} → ${f.testType}`
                    : f.status === "unmatched"
                      ? `${f.fileName} (${f.testType} — ${t("score.notInProfile")})`
                      : `${f.fileName} (${t("score.uploadError")})`}
                  color={f.status === "success" ? "primary" : f.status === "unmatched" ? "warning" : "error"}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Box>

        {testSections.map((section) => {
          const sectionTests = section.keys.filter((k) => {
            if (!profile.tests.includes(k)) return false;
            if (multiSectionKeys.includes(k)) {
              const allowedSections = profile.sectionTests?.[k];
              if (!allowedSections) return true;
              return allowedSections.includes(section.label);
            }
            return true;
          });
          if (sectionTests.length === 0) return null;
          return (
            <Box key={section.label} mt={4}>
              <Typography variant="overline" color="primary" sx={{ fontWeight: 600 }}>
                {resolveSectionLabel(section, t)}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {sectionTests.map((testName) => (
                <TestCard
                  key={testName}
                  name={testName}
                  sectionLabel={section.label}
                />
              ))}
            </Box>
          );
        })}
      </Box>
      <Box mt={5} mb={5}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleFillScores}
          disabled={isProcessing}
        >
          {isProcessing ? t("score.processing") : t("score.fill")}
        </Button>
      </Box>

      {/* Result Dialog */}
      <Dialog open={!!dialogInfo} onClose={() => setDialogInfo(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          <Typography variant="h6" color={dialogInfo?.type === "success" ? "primary" : "error"}>
            {dialogInfo?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {dialogInfo?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogInfo(null)} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
